import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core'
import { trigger, state, style, transition, animate } from '@angular/animations'
import { Arn } from 'src/utils/types'
import { ResourceType, ResourceVariant } from 'src/utils/resource-types'
import { BaseIssue, IssueDeleter, RaisedIssueScope } from 'src/app/account/services/issue'
import { SyncPair } from 'src/app/account/services/sync-pair.service'
import { ApplicationIssueDto, SyncPairIssueDto, SyncSourceIssueDto } from 'src/app/services/dtos'
import { Application } from '../../services/application.service'
import { IssueViewComponent } from '../issue-view/issue-view.component'

export interface ViewableIssue {
  issueId: string
  arn: Arn
  resourceName: string
  resourceType: ResourceType
  resourceVariant: ResourceVariant
  issue: BaseIssue
  scope: RaisedIssueScope
  deleter$: IssueDeleter
  timestamp: Date
}

export interface IssueGroup {
  issueType: string
  issues: ViewableIssue[]
  expanded: boolean
}

const accordionSlide = trigger('accordionSlide', [
  state('collapsed', style({ height: '0px', paddingTop: '0', paddingBottom: '0' })),
  state('expanded', style({ height: '*', paddingTop: '*', paddingBottom: '*' })),
  transition('collapsed <=> expanded', animate('200ms ease-in-out')),
])

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'issues-view',
  templateUrl: './issues-view.component.html',
  styleUrls: ['./issues-view.component.scss'],
  animations: [accordionSlide],
  standalone: false,
})
export class IssuesViewComponent implements OnInit, OnChanges {
  @Input() issues: Array<ViewableIssue> = null
  // tslint:disable-next-line:no-input-rename
  @Input('sync-pair') syncPair: SyncPair = null
  // tslint:disable-next-line:no-input-rename
  @Input('application') application: Application = null
  @Input() showToggleAll = true
  @ViewChildren(IssueViewComponent) issueChildren: QueryList<IssueViewComponent>

  groupedIssues: IssueGroup[] = []

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes.issues || changes.syncPair || changes.application) {
      this.computeGroups()
      this.cdr.markForCheck() // Ensure that the change detection runs when inputs change
    }
  }

  trackByFn(index: number, item: ViewableIssue) {
    return item.issueId
  }

  trackByGroup(index: number, group: IssueGroup) {
    return group.issueType
  }

  toggleGroup(group: IssueGroup) {
    group.expanded = !group.expanded
  }

  get multiIssueGroups(): IssueGroup[] {
    return this.groupedIssues.filter((g) => g.issues.length > 1)
  }

  get topLevelIssues(): IssueViewComponent[] {
    return this.issueChildren?.filter((c) => !c.grouped) ?? []
  }

  get allExpanded(): boolean {
    const groupsExpanded = this.multiIssueGroups.length === 0 || this.multiIssueGroups.every((g) => g.expanded)
    const topLevelExpanded = this.topLevelIssues.length === 0 || this.topLevelIssues.every((c) => c.panelDisplayed)
    return (this.multiIssueGroups.length > 0 || this.topLevelIssues.length > 0) && groupsExpanded && topLevelExpanded
  }

  toggleAll() {
    const expand = !this.allExpanded
    for (const group of this.multiIssueGroups) {
      group.expanded = expand
    }
    this.topLevelIssues.forEach((c) => (expand ? c.expand() : c.collapse()))
  }

  private computeGroups() {
    if (!this.issues || this.issues.length === 0) {
      this.groupedIssues = []
      return
    }

    const groupMap = new Map<string, ViewableIssue[]>()
    const groupOrder: string[] = []

    for (const issue of this.issues) {
      // Issues with the generic "Issue" fallback have no meaningful type name —
      // give each a unique key so they render as standalone flat issues
      const key =
        issue.issue.issueTypeDisplayString === BaseIssue.baseDisplayName
          ? `${BaseIssue.baseDisplayName}_${issue.issueId}`
          : issue.issue.issueTypeDisplayString
      if (!groupMap.has(key)) {
        groupMap.set(key, [])
        groupOrder.push(key)
      }
      groupMap.get(key).push(issue)
    }

    // Preserve expanded state for existing groups
    const previousState = new Map<string, boolean>()
    for (const group of this.groupedIssues) {
      previousState.set(group.issueType, group.expanded)
    }

    this.groupedIssues = groupOrder.map((issueType) => {
      const issues = groupMap.get(issueType)
      const wasExpanded = previousState.get(issueType)
      return {
        issueType,
        issues,
        // Preserve previous state, or auto-expand single-issue groups
        expanded: wasExpanded !== undefined ? wasExpanded : issues.length === 1,
      }
    })
  }
}
